import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import UserAvatar from '../../../components/UserAvatar';

const UserActivityTable = ({ users, onExport }) => {
  const [sortField, setSortField] = useState('lastLogin');
  const [sortDirection, setSortDirection] = useState('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const sortedUsers = [...users]?.sort((a, b) => {
    const aValue = a?.[sortField];
    const bValue = b?.[sortField];
    const modifier = sortDirection === 'asc' ? 1 : -1;
    
    if (typeof aValue === 'string') {
      return aValue?.localeCompare(bValue) * modifier;
    }
    return (aValue - bValue) * modifier;
  });

  const totalPages = Math.ceil(sortedUsers?.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedUsers = sortedUsers?.slice(startIndex, startIndex + itemsPerPage);

  const formatDate = (date) => {
    return new Date(date)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatTime = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  };

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      <div className="p-6 border-b border-border flex items-center justify-between">
        <h3 className="text-lg font-semibold text-card-foreground">User Activity Details</h3>
        <Button variant="outline" size="sm" iconName="Download" iconPosition="left" onClick={onExport}>
          Export CSV
        </Button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted">
            <tr>
              <th className="px-6 py-3 text-left">
                <button
                  className="flex items-center gap-2 text-sm font-semibold text-muted-foreground hover:text-foreground transition-colors"
                  onClick={() => handleSort('name')}
                >
                  User
                  <Icon name={sortField === 'name' ? (sortDirection === 'asc' ? 'ChevronUp' : 'ChevronDown') : 'ChevronsUpDown'} size={14} />
                </button>
              </th>
              <th className="px-6 py-3 text-left">
                <button
                  className="flex items-center gap-2 text-sm font-semibold text-muted-foreground hover:text-foreground transition-colors"
                  onClick={() => handleSort('lastLogin')}
                >
                  Last Login
                  <Icon name={sortField === 'lastLogin' ? (sortDirection === 'asc' ? 'ChevronUp' : 'ChevronDown') : 'ChevronsUpDown'} size={14} />
                </button>
              </th>
              <th className="px-6 py-3 text-left">
                <button
                  className="flex items-center gap-2 text-sm font-semibold text-muted-foreground hover:text-foreground transition-colors"
                  onClick={() => handleSort('totalLogins')}
                >
                  Total Logins
                  <Icon name={sortField === 'totalLogins' ? (sortDirection === 'asc' ? 'ChevronUp' : 'ChevronDown') : 'ChevronsUpDown'} size={14} />
                </button>
              </th>
              <th className="px-6 py-3 text-left">
                <button
                  className="flex items-center gap-2 text-sm font-semibold text-muted-foreground hover:text-foreground transition-colors"
                  onClick={() => handleSort('timeOnSite')}
                >
                  Time on Site
                  <Icon name={sortField === 'timeOnSite' ? (sortDirection === 'asc' ? 'ChevronUp' : 'ChevronDown') : 'ChevronsUpDown'} size={14} />
                </button>
              </th>
              <th className="px-6 py-3 text-left">
                <button
                  className="flex items-center gap-2 text-sm font-semibold text-muted-foreground hover:text-foreground transition-colors"
                  onClick={() => handleSort('quizzesCompleted')}
                >
                  Quizzes
                  <Icon name={sortField === 'quizzesCompleted' ? (sortDirection === 'asc' ? 'ChevronUp' : 'ChevronDown') : 'ChevronsUpDown'} size={14} />
                </button>
              </th>
              <th className="px-6 py-3 text-left">
                <button
                  className="flex items-center gap-2 text-sm font-semibold text-muted-foreground hover:text-foreground transition-colors"
                  onClick={() => handleSort('xpEarned')}
                >
                  XP Earned
                  <Icon name={sortField === 'xpEarned' ? (sortDirection === 'asc' ? 'ChevronUp' : 'ChevronDown') : 'ChevronsUpDown'} size={14} />
                </button>
              </th>
              <th className="px-6 py-3 text-left">
                <button
                  className="flex items-center gap-2 text-sm font-semibold text-muted-foreground hover:text-foreground transition-colors"
                  onClick={() => handleSort('engagementScore')}
                >
                  Engagement
                  <Icon name={sortField === 'engagementScore' ? (sortDirection === 'asc' ? 'ChevronUp' : 'ChevronDown') : 'ChevronsUpDown'} size={14} />
                </button>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {paginatedUsers?.map((user) => (
              <tr key={user?.id} className="hover:bg-muted/50 transition-colors">
                <td className="px-6 py-4">
                  <div className="flex items-center gap-3">
                    <UserAvatar userId={user?.id} name={user?.name} size="w-10 h-10" textSize="text-sm" />
                    <div>
                      <p className="text-sm font-medium text-card-foreground">{user?.name}</p>
                      <p className="text-xs text-muted-foreground">{user?.email}</p>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 text-sm text-card-foreground">{formatDate(user?.lastLogin)}</td>
                <td className="px-6 py-4 text-sm text-card-foreground">{user?.totalLogins}</td>
                <td className="px-6 py-4 text-sm text-card-foreground">{formatTime(user?.timeOnSite)}</td>
                <td className="px-6 py-4 text-sm text-card-foreground">{user?.quizzesCompleted}</td>
                <td className="px-6 py-4 text-sm text-card-foreground">{user?.xpEarned?.toLocaleString()}</td>
                <td className="px-6 py-4">
                  <div className="flex items-center gap-2">
                    <div className="flex-1 bg-muted rounded-full h-2 overflow-hidden">
                      <div
                        className={`h-full rounded-full ${
                          user?.engagementScore >= 80 ? 'bg-success' :
                          user?.engagementScore >= 60 ? 'bg-accent' :
                          user?.engagementScore >= 40 ? 'bg-warning' : 'bg-error'
                        }`}
                        style={{ width: `${user?.engagementScore}%` }}
                      />
                    </div>
                    <span className="text-sm font-medium text-card-foreground w-10">{user?.engagementScore}%</span>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="p-4 border-t border-border flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          Showing {startIndex + 1} to {Math.min(startIndex + itemsPerPage, sortedUsers?.length)} of {sortedUsers?.length} users
        </p>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            iconName="ChevronLeft"
            disabled={currentPage === 1}
            onClick={() => setCurrentPage(currentPage - 1)}
          >
            Previous
          </Button>
          <div className="flex items-center gap-1">
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNum;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (currentPage <= 3) {
                pageNum = i + 1;
              } else if (currentPage >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = currentPage - 2 + i;
              }
              
              return (
                <button
                  key={pageNum}
                  className={`w-8 h-8 rounded-md text-sm font-medium transition-colors ${
                    currentPage === pageNum
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:bg-muted'
                  }`}
                  onClick={() => setCurrentPage(pageNum)}
                >
                  {pageNum}
                </button>
              );
            })}
          </div>
          <Button
            variant="outline"
            size="sm"
            iconName="ChevronRight"
            iconPosition="right"
            disabled={currentPage === totalPages}
            onClick={() => setCurrentPage(currentPage + 1)}
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  );
};

export default UserActivityTable;